/** API client for AMI measured analytics endpoints. */

import { get } from './client.ts'
import type {
  AmiStatus,
  MeasuredLoadingSummary,
  MeasuredTransformerLoading,
  ModelComparisonResult,
  TransformerLoadProfile,
  VoltageViolationsSummary,
} from '../types/amiAnalytics.ts'
import type { GeoJSONFeatureCollection } from '../types/topology.ts'

function buildQS(params: Record<string, string | number | undefined | null>): string {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null) qs.set(k, String(v))
  }
  const s = qs.toString()
  return s ? `?${s}` : ''
}

export function getMeasuredLoading(params?: {
  window_days?: number
  feeder_id?: number
  avg_demand_kw?: number
}): Promise<MeasuredLoadingSummary> {
  return get<MeasuredLoadingSummary>(
    `/engineering/ami/transformers/loading${buildQS(params ?? {})}`,
  )
}

export function getLoadProfile(
  transformerId: number,
  days = 14,
): Promise<TransformerLoadProfile> {
  return get<TransformerLoadProfile>(
    `/engineering/ami/transformers/${transformerId}/load-profile${buildQS({ days })}`,
  )
}

/** AMI-measured loading for one transformer (the map detail panel's default
 *  basis). Resolves to null when the transformer has no AMI summary rows yet —
 *  the panel then falls back to the model estimate. */
export function getTransformerMeasuredLoading(
  transformerId: number,
  params?: { window_days?: number; avg_demand_kw?: number },
): Promise<MeasuredTransformerLoading | null> {
  return get<MeasuredTransformerLoading | null>(
    `/engineering/ami/transformers/${transformerId}/measured-loading${buildQS(params ?? {})}`,
  )
}

export function getVoltageViolations(params?: {
  window_days?: number
  feeder_id?: number
  limit?: number
}): Promise<VoltageViolationsSummary> {
  return get<VoltageViolationsSummary>(
    `/engineering/ami/voltage/violations${buildQS(params ?? {})}`,
  )
}

export function getModelComparison(
  feederId: number,
  windowDays = 30,
): Promise<ModelComparisonResult> {
  return get<ModelComparisonResult>(
    `/engineering/ami/voltage/model-comparison/${feederId}${buildQS({ window_days: windowDays })}`,
  )
}

export function getAmiStatus(): Promise<AmiStatus> {
  return get<AmiStatus>('/engineering/ami/status')
}

// Map layers (GeoJSON) — mirroring the existing map API helpers in engineering.ts

export function getMeasuredLoadingGeoJSON(params?: {
  window_days?: number
  feeder_id?: number
  // Threads into the hybrid adder; keep it equal to the /map/transformers/loading
  // layer's avg_demand_kw so both measured surfaces color a hybrid bank alike.
  avg_demand_kw?: number
}): Promise<GeoJSONFeatureCollection> {
  return get<GeoJSONFeatureCollection>(
    `/map/transformers/measured-loading${buildQS(params ?? {})}`,
  )
}

export function getVoltageViolationsGeoJSON(params?: {
  window_days?: number
  feeder_id?: number
  limit?: number
}): Promise<GeoJSONFeatureCollection> {
  return get<GeoJSONFeatureCollection>(
    `/map/meters/voltage-violations${buildQS(params ?? {})}`,
  )
}
